import { useEffect, useRef, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AccountNotFound } from './AccountNotFound';
import { PageErrorBoundary } from '../../ui/PageErrorBoundary';
import { useAccount } from '../../contexts/AccountContext';
import { useFeatures } from '../../hooks/useFeatures';
import { AccountLayout } from './AccountLayout';
import { AccountAuth } from './AccountAuth';
import { PasswordResetForm } from './PasswordResetForm';
import { SkinJourney } from './SkinJourney';
import { AtHome } from './AtHome';
import { Appointments } from './Appointments';
import { PhotoDiary } from './PhotoDiary';
import { Orders } from './Orders';
import { Profile } from './Profile';

/** Mijn omgeving: niet ingelogd → inloggen op dezelfde plek, daarna direct door. */
export function Account() {
  const {
    status,
    signedIn,
    resetToken
  } = useAccount();
  const features = useFeatures();
  const {
    pathname
  } = useLocation();
  const navigate = useNavigate();
  // Na een verlopen herstellink opent het inlogscherm meteen op "wachtwoord vergeten".
  const [authStart, setAuthStart] = useState<'login' | 'reset'>('login');
  useEffect(() => {
    if (signedIn) setAuthStart('login');
  }, [signedIn]);

  // Uitgelogd (knop of verlopen sessie): terug naar het begin, zodat de
  // volgende klant op haar overzicht start en niet op het onderdeel van de vorige.
  const wasSignedIn = useRef(signedIn);
  useEffect(() => {
    if (wasSignedIn.current && !signedIn && pathname !== '/account') navigate('/account', {
      replace: true
    });
    wasSignedIn.current = signedIn;
  }, [signedIn, pathname, navigate]);

  // Sessie wordt nog gecontroleerd: geen inlogscherm laten flitsen bij een ingelogde klant.
  if (status === 'loading') return <div role="status" aria-live="polite" className="flex min-h-[60vh] w-full items-center justify-center bg-sand" data-id="element-5626" data-mp="1tsk3w9bkakft">
        <span className="sr-only" data-id="element-5627" data-mp="355mkh4dnv9zz">Mijn omgeving laden…</span>
        <span className="h-px w-24 overflow-hidden bg-line" aria-hidden="true" data-id="element-5628" data-mp="355cv9j0fx6vc">
          <span className="block h-full w-1/3 bg-ink motion-safe:animate-pulse" data-id="element-5629" data-mp="21txcts6ff6cw" />
        </span>
      </div>;
  // Link uit de herstelmail: eerst een nieuw wachtwoord, ook als ze al ingelogd was.
  if (resetToken !== null) return <PasswordResetForm onRequestNew={() => setAuthStart('reset')} data-id="element-5630" data-mp="2bqsbqc6m38rp" />;
  if (!signedIn) return <AccountAuth key={authStart} initialMode={authStart} data-id="element-5631" data-mp="2ftozehb0dv2i" />;
  return <AccountLayout data-id="element-5632" data-mp="1j5ab9jngkz42">
      <PageErrorBoundary key={pathname} body="Kies een ander onderdeel in het menu, of probeer het opnieuw." data-id="element-5633" data-mp="1j5sh97zxarp6">
        <Routes data-id="element-5634" data-mp="1o3k5ns1czvom">
          <Route index element={<SkinJourney data-id="element-5636" data-mp="1s55pxyg83c8u" />} data-id="element-5635" data-mp="1o0xyhtidri6x" />
          <Route path="afspraken" element={<Appointments data-id="element-5638" data-mp="1s2szewweftk6" />} data-id="element-5637" data-mp="1s3ulrxiv82h7" />
          <Route path="thuis" element={<AtHome data-id="element-5640" data-mp="1wwnsgqxw9kvx" />} data-id="element-5639" data-mp="1wvdqvrru1ukj" />
          <Route path="dagboek" element={features.diary ? <PhotoDiary data-id="element-5642" data-mp="10dzl54pk4qd8" /> : <AccountNotFound data-id="element-5643" data-mp="10ej5es2ojquf" />} data-id="element-5641" data-mp="1wx6x55yjacs0" />
          <Route path="bestellingen" element={<Orders data-id="element-5645" data-mp="14fbffnjdlzrr" />} data-id="element-5644" data-mp="10d80goxi0d1q" />
          <Route path="gegevens" element={<Profile data-id="element-5647" data-mp="14e0a3jaa8jyj" />} data-id="element-5646" data-mp="14et89vv6e152" />
          <Route path="*" element={<AccountNotFound data-id="element-5649" data-mp="wvtv47yb55u5" />} data-id="element-5648" data-mp="14d8fobsf8s9u" />
        </Routes>
      </PageErrorBoundary>
    </AccountLayout>;
}